import React, {FunctionComponent, useState} from 'react';

import { ErrorMessage, FormInput, TimeDurationPicker } from 'components';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader} from '@cfa/react-components';
import {AsyncStatus} from 'enums';
import { ITemplate } from 'interfaces';
import { useForm } from 'react-hook-form';
import { ManufacturersService, parseAxiosError, TemplatesService, UpdateFilesService, UpdateTypesService } from 'services';
import { UpdatePageModel } from '../UpdatePageModel';

interface ISaveTemplateModalProps {
    model: UpdatePageModel,
    onClose: () => void,
    show: boolean,
}

export const SaveTemplateModal: FunctionComponent<ISaveTemplateModalProps> = function(props) {
    const { model, show, onClose } = props;
    
    const form = useForm<ITemplate>({ mode: "onTouched" });

    const [loadStatus, setLoadStatus] = useState(AsyncStatus.Initial);
    const [errorMessage, setErrorMessage] = useState('');

    const [durationMilliseconds, setDurationMilliseconds] = useState(
        Math.max(0, model.update.stopAt.getTime() - model.update.startAt.getTime())
    )
    
    function onChangeDuration(newDurationMilli: number) {
        setDurationMilliseconds(newDurationMilli);
    }

    function tryHide() {
        // Prevent closing the modal if an API call is in progress.
        if (loadStatus !== AsyncStatus.Loading) {
            onClose();
        }
    }

    function onConfirm() {
        if (loadStatus === AsyncStatus.Loading || !form.formState.isValid) return;

        setErrorMessage('');
        setLoadStatus(AsyncStatus.Loading);
        
        form.handleSubmit((formData: ITemplate) => {

            let payload: ITemplate = {
                name: formData.name,
                description: formData.description,
                manufacturerID: model.update.manufacturerId,
                updateTypeID: model.update.updateTypeId,
                updateFileID: model.update.updateFileId,
                deviceModelIDs: model.update.deviceModelIds,
                duration: durationMilliseconds,
                notifiers: model.update.notificationEmails,
            };

            console.log(payload);
            
            TemplatesService.createTemplateFromUpdate(payload)
                .then(() => {
                    setLoadStatus(AsyncStatus.Success);
                    onClose();
                })
                .catch((err) => {
                    setErrorMessage(parseAxiosError(err));
                    setLoadStatus(AsyncStatus.Error);
                });
            
        })();
    }

    function getModelNames() {
        let names = [];

        model.update.deviceModelIds.forEach((id) => {
            names.push(ManufacturersService.modelsMap[id].name);
        })

        return names.join(", ");
    }

    return (
        <Modal show={show} onClose={tryHide} size='lg'>
            <ModalHeader>
                Save as Template
            </ModalHeader>
            <ModalBody>
                <div>
                    {loadStatus === AsyncStatus.Error && (
                        <ErrorMessage message={errorMessage} />
                    )}
                    <div className="row">
                        <div className="col">
                            <FormInput
                                form={form}
                                label="Name"
                                name="name"
                                registerOptions={{ required: true }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <FormInput
                                form={form}
                                label="Description"
                                name="description"
                                registerOptions={{ required: true }}
                            />
                        </div>
                        <div className="col">
                            <TimeDurationPicker
                                durationMilliseconds={durationMilliseconds}
                                label="Duration"
                                onChange={onChangeDuration}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <FormInput
                                form={form}
                                label="Manufacturer"
                                name="manufacturerID"
                                defaultValue={model.update.manufacturerId === 0 ? "" : 
                                    ManufacturersService.manufacturersMap[model.update.manufacturerId].name}
                                registerOptions={{ required: true }}
                                disabled
                            />
                        </div>
                        <div className="col">
                            <FormInput
                                form={form}
                                label="Models"
                                name="deviceModelIDs"
                                defaultValue={model.update.deviceModelIds.length === 0 ? "" : 
                                    getModelNames()}
                                registerOptions={{ required: true }}
                                disabled
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <FormInput
                                form={form}
                                label="Update Type"
                                name="updateTypeID"
                                defaultValue={model.update.updateTypeId === 0 ? "" : 
                                    UpdateTypesService.updateTypesMap[model.update.updateTypeId].description}
                                registerOptions={{ required: true }}
                                disabled
                            />
                        </div>
                        <div className="col">
                            <FormInput
                                form={form}
                                label="Update File Version"
                                name="updateFileID"
                                defaultValue={model.update.updateFileVersion === "" ? "" : 
                                    UpdateFilesService.allUpdateFilesMap[model.update.updateFileId].version}
                                registerOptions={{ required: true }}
                                disabled
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <FormInput
                                form={form}
                                label="Notification Emails"
                                name="notifiers"
                                defaultValue={model.update.notificationEmails.length === 0 ? "" : 
                                    model.update.notificationEmails.join(", ")}
                                registerOptions={{ required: true }}
                                disabled
                            />
                        </div>
                    </div>
                </div>
                
            </ModalBody>
            <ModalFooter>
                <Button
                    variant="outlined"
                    onClick={tryHide}
                    disabled={loadStatus === AsyncStatus.Loading}
                >
                    Cancel
                </Button>
                <Button
                    style={{marginLeft: "10px"}}
                    onClick={onConfirm}
                    loading={loadStatus === AsyncStatus.Loading}
                    disabled={!form.formState.isValid}
                >
                    {loadStatus === AsyncStatus.Loading && 'Submitting...'}
                    {loadStatus !== AsyncStatus.Loading && 'Submit Template'}
                </Button>
            </ModalFooter>
        </Modal>
    );
}